import { Store } from "redux";
import { reportError } from "../../components/ticketing/basket-v2/errorReporting";
import { ADD_PROMO, REMOVE_PROMO } from "./store";
import { constants } from "../../components/ticketing/common";
import { postJsonWithResponse, deleteJsonWithResponse, trackEvent, EventEmitter, trackCheckoutEvent } from "../../utils";
import { ApplyPromoCodeRequest } from "../../components/ticketing/basket-v2/api/models";
import { evaluateResponse, removeSpaces } from "./utils";
import { events } from "../constants";

export class Promo {
  promo: HTMLElement | null;
  appliedPromos: HTMLElement | null;
  store: Store;
  applyButton: HTMLButtonElement | null;
  promoInput: HTMLInputElement | null;
  checkoutPageId: HTMLInputElement | null;
  defaultError = { code: 400, message: "Error applying/removing promo code" };

  constructor(selector: string, store: Store) {
    this.store = store;
    this.promo = document.querySelector(selector);
    this.appliedPromos = document.querySelector("#appliedPromocodes");
    this.checkoutPageId = document.querySelector("#checkoutPageId");

    if (!this.promo) return;

    this.applyButton = this.promo.querySelector<HTMLButtonElement>("[data-promo-apply]");
    this.promoInput = this.promo.querySelector<HTMLInputElement>("[data-promo-input]");
    this.promoInput.style.backgroundImage = 'none';
    this.promoInput.style.boxShadow = 'none';
    this.bindEvents();
  }

  bindEvents() {
    this.promoInput.addEventListener("blur", () => {
      if (this.promoInput.value == "") {
        this.promoInput.classList.remove("is-invalid");
      }

      this.promoInput.value = removeSpaces(this.promoInput.value);
    });

    if (this.applyButton) {
      this.applyButton.addEventListener("click", this.handleApplyPromo);
    }
    this.bindRemovePromoButtons();
  }

  bindRemovePromoButtons() {
    if (this.appliedPromos) {
      const removeBtns = this.appliedPromos.querySelectorAll("[data-promo-remove]");
      for (let i = 0; i < removeBtns.length; i++) {
        removeBtns[i].addEventListener("click", this.handleRemovePromo);
      }
    }
  }

  handleApplyPromo = async () => {

    try {

      EventEmitter.emit(events.ORDER_UPDATE_START);

      const promoCode = removeSpaces(this.promoInput?.value) ?? "";

      if (!promoCode || promoCode === "") {
        this.promoInput.classList.add("is-invalid");
        return;
      } else {
        this.promoInput.classList.remove("is-invalid");
      }

      let promoRequest: ApplyPromoCodeRequest = {
        checkoutPageId: parseInt(this.checkoutPageId?.value ?? "0"),
        promoCode: promoCode
      }

      const promoResponse = await postJsonWithResponse(constants.urls.basketV2.promo_code, promoRequest);
      const responseData = await evaluateResponse(promoResponse, this.promoInput, this.defaultError.message, events.ORDER_UPDATE_END);
      
      if (responseData?.status !== 200) {
        trackCheckoutEvent({
          message_name: responseData?.trackingMessageName ?? "promo_code_error",
          message_text: responseData?.content ?? this.defaultError.message,
          message_type: "error",
          checkout_step: "checkout"
        });
        return;          
      }

      if (this.appliedPromos) {
        if (this.promoInput)
          this.promoInput.value = "";

        this.appliedPromos.innerHTML = responseData.content;

        this.bindRemovePromoButtons();

        const appliedItemSelector = `div[data-promo-added="${promoCode}"]`;
        const appliedPromoItem = this.appliedPromos.querySelector<HTMLElement>(appliedItemSelector) ?? new HTMLElement();
        const appliedAmount = appliedPromoItem.dataset.promoAmount ?? "0.00";

        this.store.dispatch({
          type: ADD_PROMO,
          payload: {
            code: promoCode,
            discount: 0,
            amount: parseFloat(appliedAmount)
          },
        });

        trackEvent({
          event: 'checkout',
          category: 'Checkout',
          action: 'Add Promo',
          label: promoCode
        });
      }

    } catch (e) {
      const message = "An error occurred applying the promo code"; 

      reportError(500, message);
      trackCheckoutEvent({
          message_name: "promo_code_error",
          message_text: message,
          message_type: "error",
          checkout_step: "basket"
      });

    }
    EventEmitter.emit(events.ORDER_UPDATE_END);
  };

  handleRemovePromo = async (e: Event) => {
    //console.log(e.target);
    try {

      if (e.target instanceof HTMLElement && "promoRemove" in e.target.dataset) {

        EventEmitter.emit(events.ORDER_UPDATE_START);

        let promoCode = e.target.dataset.promoCode;
        let promoRequest: ApplyPromoCodeRequest = {
          checkoutPageId: parseInt(this.checkoutPageId?.value ?? "0"),
          promoCode: promoCode ?? ""
        }

        const promoResponse = await deleteJsonWithResponse(constants.urls.basketV2.promo_code, promoRequest);
        const responseData = await evaluateResponse(promoResponse, this.promoInput, this.defaultError.message, events.ORDER_UPDATE_END);

        if (responseData?.status !== 200) {
          trackCheckoutEvent({
            message_name: responseData?.trackingMessageName ?? "promo_code_error",
            message_text: responseData?.content ?? this.defaultError.message,
            message_type: "error",
            checkout_step: "checkout"
          });
          return;          
        }

        if (this.appliedPromos) {
          this.appliedPromos.innerHTML = await responseData.content;
          this.bindRemovePromoButtons();

          this.store.dispatch({
            type: REMOVE_PROMO,
          });

          trackEvent({
            event: 'checkout',
            category: 'Checkout',
            action: 'Remove Promo',
            label: promoCode
          });
        }
      }
    } catch (e) {
      reportError(500, "An error occurred removing the promo code");
      EventEmitter.emit(events.ORDER_UPDATE_END);
      return;
    }
  };
}